import React, { useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
} from 'chart.js';
import 'chartjs-adapter-moment';
import { AppAuthStateProps } from '../../types/propTypes';
import { dashboardLLMCache } from '../dashboard/llm/cache';
import { readBinaryFile } from '../../amplify/s3/storage';
import PatientReportedHeatmap from './PatientReportedHeatmap';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
);

// TypeScript interfaces
interface S3Config {
    bucketName: string;
    scanFileName: string;
    explainFileName: string;
    region: string;
}

interface BaselineData {
    patient_summary?: string;
    index_visit_summary?: string;
    revisit_risk_factors?: string[];
    initial_monitoring_assessment?: {
        baseline_status?: string;
        monitoring_priorities?: string;
    };
}

interface DailyAssessment {
    day: number;
    date: string;
    assessment_data?: {
        trajectory_classification?: string;
        trajectory_justification?: string;
        lidia_message?: string;
    };
    trajectory_classification?: string;
    trajectory_justification?: string;
    lidia_message?: string;
}

interface ScanCompassData {
    metadata?: {
        study_id?: string;
        mrn?: string;
        generation_time_pt?: string;
    };
    daily_monitoring_aggregates?: {
        monitoring_aggregates?: Record<string, Array<{ date: string; value: number }>>;
    };
    survey_daily?: Array<{
        start_date?: string;
        eq_6?: number;
        se_c?: number;
        eq_1?: number;
        eq_2?: number;
        eq_3?: number;
        eq_4?: number;
        eq_5?: number;
        visit_2?: number;
        visit_4?: number;
    }>;
}

interface ExplainCompassData {
    explanation_metadata?: {
        study_id?: string;
        explanation_time?: string;
    };
    baseline_assessment?: {
        baseline_data?: BaselineData;
    };
    daily_assessments?: DailyAssessment[];
}

interface MetricConfig {
    label: string;
    shortLabel: string;
    description: string;
    units: string;
    color: string;
    icon: string;
}

const CompassProspectiveDashboard = (props: AppAuthStateProps) => {
    const [scanData, setScanData] = useState<ScanCompassData | null>(null);
    const [explainData, setExplainData] = useState<ExplainCompassData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Styles object for inline styling (replacing Tailwind)
    const styles = {
        container: {
            marginTop: '48px',
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            padding: '20px'
        },
        card: {
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '24px'
        },
        formGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '16px'
        },
        input: {
            padding: '8px',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            width: '100%'
        },
        button: {
            backgroundColor: '#007bff',
            color: 'white',
            padding: '8px 24px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
        },
        buttonDisabled: {
            backgroundColor: '#6c757d',
            cursor: 'not-allowed'
        },
        error: {
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            color: '#721c24',
            borderRadius: '4px'
        },
        collapsibleHeader: {
            padding: '16px',
            borderBottom: '1px solid #dee2e6',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa'
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0'
        },
        timelineContainer: {
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: '16px'
        },
        timelineCard: {
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '16px',
            height: 'fit-content'
        },
        dayItem: {
            padding: '8px',
            borderRadius: '8px',
            border: '2px solid transparent',
            marginBottom: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
        },
        dayItemSelected: {
            borderColor: '#495057'
        },
        trajectoryImproving: {
            backgroundColor: '#d4edda',
            borderColor: '#c3e6cb'
        },
        trajectoryStable: {
            backgroundColor: '#d1ecf1',
            borderColor: '#b6d4d8'
        },
        trajectoryWorsening: {
            backgroundColor: '#f8d7da',
            borderColor: '#f5c6cb'
        },
        trajectoryDefault: {
            backgroundColor: '#f8f9fa',
            borderColor: '#e9ecef'
        },
        badge: {
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 'bold'
        },
        badgeImproving: {
            backgroundColor: '#d4edda',
            color: '#155724'
        },
        badgeStable: {
            backgroundColor: '#d1ecf1',
            color: '#0c5460'
        },
        badgeWorsening: {
            backgroundColor: '#f8d7da',
            color: '#721c24'
        },
        badgeDefault: {
            backgroundColor: '#e2e3e5',
            color: '#383d41'
        },
        metricGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px'
        },
        metricCard: {
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #dee2e6',
            cursor: 'pointer',
            transition: 'all 0.2s',
            backgroundColor: 'white'
        },
        metricCardSelected: {
            backgroundColor: '#e3f2fd',
            borderColor: '#2196f3',
            boxShadow: '0 2px 8px rgba(33,150,243,0.3)'
        },
        chartContainer: {
            height: '300px',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
        },
        chartContainerLarge: {
            height: '400px'
        },
        placeholder: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#6c757d',
            textAlign: 'center' as const
        }
    };

    useEffect(() => {
        fetchFromS3()
    }, []);

    // Fetch data from S3
    const fetchFromS3 = async (): Promise<void> => {

        setLoading(true);
        setError(null);

        try {
            const decoder = new TextDecoder();

            // Fetch scan compass file
            const remoteCacheData = await readBinaryFile(`llm_dashboard/compass=COM0061/scan_compass_prospective.json`, props.tenant, `ed-llm-dashboard`);
            const scanJson = JSON.parse(decoder.decode(remoteCacheData));

            // Fetch explain compass file
            const remoteExplainCacheData = await readBinaryFile(`llm_dashboard/compass=COM0061/explain_compass_prospective.json`, props.tenant, `ed-llm-dashboard`);
            const explainJson = JSON.parse(decoder.decode(remoteExplainCacheData));

            setScanData(scanJson);
            setExplainData(explainJson);
        } catch (err) {
            setError(`Error fetching data: ${(err as Error).message}`);
        } finally {
            setLoading(false);
        }
    };

    // Collapsible section component
    const CollapsibleSection: React.FC<{
        title: string;
        children: React.ReactNode;
        defaultOpen?: boolean;
    }> = ({ title, children, defaultOpen = true }) => {
        const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

        return (
            <div style={styles.card}>
                <div
                    style={styles.collapsibleHeader}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <h2 style={styles.title}>{title}</h2>
                    <span>{isOpen ? '▼' : '▶'}</span>
                </div>
                {isOpen && (
                    <div style={{ padding: '16px' }}>
                        {children}
                    </div>
                )}
            </div>
        );
    };

    // Baseline Assessment Section
    const BaselineAssessmentSection: React.FC = () => {
        const baselineData = explainData?.baseline_assessment?.baseline_data;
        const monitoringAssessment = baselineData?.initial_monitoring_assessment;

        if (!baselineData) {
            return <div style={{ padding: '16px', color: '#6c757d' }}>No baseline assessment data available.</div>;
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {baselineData.patient_summary && (
                    <div style={{ padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>Patient Summary</h3>
                        <p style={{ margin: '0', color: '#212529' }}>{baselineData.patient_summary}</p>
                    </div>
                )}

                {baselineData.index_visit_summary && (
                    <div style={{ padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>Index Visit Summary</h3>
                        <p style={{ margin: '0', color: '#212529' }}>{baselineData.index_visit_summary}</p>
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {baselineData.revisit_risk_factors && (
                        <div style={{ padding: '12px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '8px' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: '#721c24' }}>Revisit Risk Factors</h3>
                            <ul style={{ margin: '0', padding: '0', listStyle: 'none' }}>
                                {baselineData.revisit_risk_factors.map((factor, idx) => (
                                    <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px' }}>
                                        <span style={{ marginRight: '8px', color: '#dc3545' }}>•</span>
                                        <span style={{ color: '#495057' }}>{factor}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {monitoringAssessment && (
                        <div style={{ padding: '12px', backgroundColor: '#d1ecf1', border: '1px solid #b6d4d8', borderRadius: '8px' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: '#0c5460' }}>Monitoring Priorities</h3>
                            <p style={{ margin: '0 0 8px 0', color: '#495057' }}>{monitoringAssessment.baseline_status}</p>
                            <p style={{ margin: '0', color: '#495057' }}>{monitoringAssessment.monitoring_priorities}</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Daily Timeline Section
    const DailyTimelineSection: React.FC = () => {
        const [selectedDay, setSelectedDay] = useState<number>(0);
        const [expandedDay, setExpandedDay] = useState<number>(-1);
        const [timelineViewMode, setTimelineViewMode] = useState<'provider' | 'patient'>('provider');
        const [selectedMetric, setSelectedMetric] = useState<string>('hr');

        const dailyAssessments = explainData?.daily_assessments || [];

        // Metric configurations
        const metricConfigs: Record<string, MetricConfig> = {
            hr: {
                label: 'Heart Rate',
                shortLabel: 'HR',
                description: 'Average daily heart rate',
                units: 'bpm',
                color: '#dc3545',
                icon: '💓'
            },
            rr: {
                label: 'Respiratory Rate',
                shortLabel: 'RR',
                description: 'Average daily respiratory rate',
                units: 'breaths/min',
                color: '#007bff',
                icon: '🫁'
            },
            hrv: {
                label: 'Heart Rate Variability',
                shortLabel: 'HRV',
                description: 'Beat-to-beat variation',
                units: 'ms',
                color: '#6f42c1',
                icon: '⚡'
            },
            temp: {
                label: 'Temperature',
                shortLabel: 'Temp',
                description: 'Skin temperature',
                units: '°C',
                color: '#fd7e14',
                icon: '🌡️'
            },
            steps: {
                label: 'Steps',
                shortLabel: 'Steps',
                description: 'Daily step count',
                units: 'steps',
                color: '#28a745',
                icon: '👟'
            },
            sleep: {
                label: 'Sleep',
                shortLabel: 'Sleep',
                description: 'Hours of sleep',
                units: 'hours',
                color: '#6610f2',
                icon: '🌙'
            }
        };

        // Helper functions for trajectory colors
        const getTrajectoryStyle = (trajectory?: string) => {
            switch (trajectory?.toLowerCase()) {
                case 'improving': return styles.trajectoryImproving;
                case 'stable': return styles.trajectoryStable;
                case 'worsening':
                case 'deteriorating': return styles.trajectoryWorsening;
                default: return styles.trajectoryDefault;
            }
        };

        const getTrajectoryBadgeStyle = (trajectory?: string) => {
            switch (trajectory?.toLowerCase()) {
                case 'improving': return styles.badgeImproving;
                case 'stable': return styles.badgeStable;
                case 'worsening':
                case 'deteriorating': return styles.badgeWorsening;
                default: return styles.badgeDefault;
            }
        };

        // Generate chart data for selected metric
        const generateChartData = () => {
            const monitoringData = scanData?.daily_monitoring_aggregates?.monitoring_aggregates;
            if (!monitoringData) return null;

            // Map metric keys to data keys in scan data
            const metricDataMap: Record<string, string[]> = {
                hr: ['pulse_rate_resting_wake_avg', 'pulse_rate_all_sleep_avg'],
                rr: ['respiratory_rate_resting_wake_avg', 'respiratory_rate_all_sleep_avg'],
                hrv: ['prv_resting_wake_avg', 'prv_all_sleep_avg'],
                temp: ['temperature_resting_wake_avg', 'temperature_all_sleep_avg'],
                steps: ['step_counts'],
                sleep: ['total_sleep_time', 'sleep_hours']
            };

            const dataKeys = metricDataMap[selectedMetric] || [];
            const datasets: any[] = [];
            const colors = ['#ff6384', '#36a2eb', '#cc65fe', '#ffce56'];

            dataKeys.forEach((key, index) => {
                const data = monitoringData[key];
                if (data && data.length > 0) {
                    datasets.push({
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
                        data: data.map(point => ({
                            x: point.date,
                            y: point.value
                        })),
                        borderColor: colors[index % colors.length],
                        backgroundColor: colors[index % colors.length] + '20',
                        fill: false,
                        tension: 0.1
                    });
                }
            });

            return datasets.length > 0 ? { datasets } : null;
        };

        const chartData = generateChartData();

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    type: 'time' as const,
                    time: {
                        unit: 'day' as const
                    },
                    title: {
                        display: true,
                        text: 'Date'
                    }
                },
                y: {
                    title: {
                        display: true,
                        text: `${metricConfigs[selectedMetric]?.label} (${metricConfigs[selectedMetric]?.units})`
                    }
                }
            },
            plugins: {
                legend: {
                    position: 'top' as const
                },
                tooltip: {
                    mode: 'index' as const,
                    intersect: false
                }
            }
        };

        return (
            <div style={styles.timelineContainer}>
                {/* Left Column: Daily Timeline */}
                <div style={styles.timelineCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #dee2e6' }}>
                        <h3 style={{ margin: '0', fontWeight: 'bold', color: '#495057' }}>Daily Timeline</h3>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                style={{
                                    ...styles.button,
                                    backgroundColor: timelineViewMode === 'provider' ? '#ffc107' : '#6c757d',
                                    color: timelineViewMode === 'provider' ? '#212529' : 'white',
                                    padding: '4px 12px',
                                    fontSize: '12px'
                                }}
                                onClick={() => setTimelineViewMode('provider')}
                            >
                                Provider
                            </button>
                            <button
                                style={{
                                    ...styles.button,
                                    backgroundColor: timelineViewMode === 'patient' ? '#ffc107' : '#6c757d',
                                    color: timelineViewMode === 'patient' ? '#212529' : 'white',
                                    padding: '4px 12px',
                                    fontSize: '12px'
                                }}
                                onClick={() => setTimelineViewMode('patient')}
                            >
                                Patient
                            </button>
                        </div>
                    </div>

                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {dailyAssessments.map((assessment, idx) => {
                            const assessmentData = assessment.assessment_data || assessment;
                            const isSelected = selectedDay === idx;
                            const isExpanded = expandedDay === idx;
                            const trajectory = assessmentData.trajectory_classification;

                            return (
                                <div
                                    key={idx}
                                    style={{
                                        ...styles.dayItem,
                                        ...getTrajectoryStyle(trajectory),
                                        ...(isSelected ? styles.dayItemSelected : {})
                                    }}
                                    onClick={() => {
                                        setSelectedDay(idx);
                                        setExpandedDay(expandedDay === idx ? -1 : idx);
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ color: '#6c757d', fontSize: '12px', cursor: 'pointer' }}>
                                                {isExpanded ? '▼' : '▶'}
                                            </span>
                                            <span style={{ fontWeight: 'bold', color: '#212529' }}>Day {assessment.day}</span>
                                            <span style={{ fontSize: '14px', color: '#6c757d' }}>({assessment.date})</span>
                                        </div>
                                        <span style={{ ...styles.badge, ...getTrajectoryBadgeStyle(trajectory) }}>
                                            {trajectory}
                                        </span>
                                    </div>

                                    {isExpanded && (
                                        <div style={{ paddingTop: '8px', paddingLeft: '16px' }}>
                                            {timelineViewMode === 'provider' ? (
                                                assessmentData.trajectory_justification && (
                                                    <p style={{ margin: '0', fontSize: '14px', color: '#495057' }}>
                                                        {assessmentData.trajectory_justification}
                                                    </p>
                                                )
                                            ) : (
                                                assessmentData.lidia_message && (
                                                    <p style={{ margin: '0', fontSize: '14px', color: '#495057', fontStyle: 'italic' }}>
                                                        {assessmentData.lidia_message}
                                                    </p>
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Right Column: Charts and Metrics */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Patient-reported metrics chart */}

                    {
                        scanData && (
                            <PatientReportedHeatmap scanData={scanData} dailyAssessments={dailyAssessments} selectedDay={selectedDay} />
                        )
                    }

                    {/* Device-monitored metrics chart */}
                    <div style={styles.chartContainer}>
                        <h3 style={{ margin: '0 0 16px 0', fontWeight: 'bold', color: '#495057' }}>
                            {metricConfigs[selectedMetric]
                                ? `${metricConfigs[selectedMetric].label} (${metricConfigs[selectedMetric].units})`
                                : "Device-monitored Metrics"
                            }
                        </h3>
                        <div style={{ height: 'calc(100% - 40px)' }}>
                            {chartData ? (
                                <Line data={chartData} options={chartOptions} />
                            ) : (
                                <div style={styles.placeholder}>
                                    <div>
                                        <div style={{ fontSize: '48px', marginBottom: '8px' }}>{metricConfigs[selectedMetric]?.icon || '📈'}</div>
                                        <p style={{ margin: '8px 0' }}>Device monitoring for {metricConfigs[selectedMetric]?.label || 'selected metric'}</p>
                                        <p style={{ margin: '0', fontSize: '14px' }}>No data available for this metric</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Metric selection cards */}
                    <div style={styles.card}>
                        <div style={styles.metricGrid}>
                            {Object.entries(metricConfigs).map(([key, config]) => {
                                const isSelected = selectedMetric === key;

                                return (
                                    <div
                                        key={key}
                                        style={{
                                            ...styles.metricCard,
                                            ...(isSelected ? styles.metricCardSelected : {})
                                        }}
                                        onClick={() => setSelectedMetric(key)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '16px' }}>{config.icon}</span>
                                            <span style={{
                                                fontWeight: 'bold',
                                                color: isSelected ? '#1976d2' : '#212529'
                                            }}>
                                                {config.shortLabel}
                                            </span>
                                            <span style={{ fontSize: '14px', color: '#6c757d' }}>: {config.description}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Get metadata for display
    const getMetadata = () => {
        const scanMeta = scanData?.metadata || {};
        const explainMeta = explainData?.explanation_metadata || {};

        return {
            studyId: scanMeta.study_id || explainMeta.study_id || 'Unknown',
            mrn: scanMeta.mrn || 'Unknown',
            reportDate: scanMeta.generation_time_pt || explainMeta.explanation_time || new Date().toISOString()
        };
    };

    const metadata = getMetadata();

    return (
        <div style={styles.container}>
            {(scanData || explainData) && (
                <>
                    <header style={{ ...styles.card, marginBottom: '24px' }}>
                        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#212529', margin: '0 0 8px 0' }}>
                            COMPASS - Prospective Monitoring Report
                        </h1>
                        <div style={{ fontSize: '16px', color: '#6c757d', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                            <span>Study ID: {metadata.studyId}</span>
                            <span>MRN: {metadata.mrn}</span>
                            <span>Report Generated: {metadata.reportDate}</span>
                        </div>
                    </header>

                    <CollapsibleSection title="Baseline Assessment" defaultOpen={true}>
                        <BaselineAssessmentSection />
                    </CollapsibleSection>

                    <CollapsibleSection title="Daily Monitoring Timeline" defaultOpen={true}>
                        <DailyTimelineSection />
                    </CollapsibleSection>
                </>
            )}

            {
                loading && (
                    <>Loading...</>
                )
            }
            {!scanData && !explainData && !loading && (
                <div style={{ ...styles.card, textAlign: 'center', padding: '48px' }}>
                    <div style={{ color: '#6c757d' }}>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>📊</div>
                        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>COMPASS Dashboard</h2>
                        <p style={{ margin: '0' }}>Configure S3 settings above to load patient monitoring data</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompassProspectiveDashboard;
